import { useTranslation } from "react-i18next";
import { DeleteOutlined, DownOutlined } from "@ant-design/icons";
import type { KeyboardLayout } from "../../../../api/configuration";
import { useUpdateUserConfiguration } from "../../../../hooks/useConfiguration";

interface SearchKeyboardProps {
  value: string;
  // Дължина на префикса на value, който има съвпадения; буквите след него
  // (заявка без съвпадения) се показват в червено, както в търсачката.
  matchedPrefixLength?: number;
  /** Личната настройка `keyboardLayout` — сменя се само оттук (БДС |
   *  Фонетична | ABC) и се записва директно в базата. */
  layout: KeyboardLayout;
  onInput: (text: string) => void;
  onBackspace: () => void;
  onClear: () => void;
  onHide: () => void;
}

// Редовете на трите подредби (главни букви — както са на физическата
// клавиатура). БДС и фонетична са кирилица, латиница е QWERTY.
const LAYOUT_ROWS: Record<KeyboardLayout, string[][]> = {
  bds: [
    ["У", "Е", "И", "Ш", "Щ", "К", "С", "Д", "З", "Ц"],
    ["Ь", "Я", "А", "О", "Ж", "Г", "Т", "Н", "В", "М", "Ч"],
    ["Ю", "Й", "Ъ", "Э", "Ф", "Х", "П", "Р", "Л", "Б"],
  ],
  phonetic: [
    ["Я", "В", "Е", "Р", "Т", "Ъ", "У", "И", "О", "П", "Ш", "Щ"],
    ["А", "С", "Д", "Ф", "Г", "Х", "Й", "К", "Л", "Ю"],
    ["З", "Ь", "Ц", "Ж", "Б", "Н", "М", "Ч"],
  ],
  latin: [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["Z", "X", "C", "V", "B", "N", "M"],
  ],
};

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

const KEY =
  "h-11 min-w-0 rounded-[10px] border border-pos-ctl bg-pos-card text-[15px] font-semibold text-pos-ink cursor-pointer select-none transition-colors hover:bg-pos-marked hover:border-pos-accent/40 active:bg-pos-marked";
const ACTION =
  "h-11 rounded-[10px] border border-pos-ctl bg-pos-card px-3 text-[13px] font-semibold text-pos-ink2 cursor-pointer select-none transition-colors hover:bg-pos-panel";

/**
 * Екранна клавиатура за търсачката на каталога (F8 / бутонът „Клавиатура"
 * във футъра). Пише в СЪЩИЯ searchTerm като физическата клавиатура; ляга в
 * долната част на колоната с продуктите (ItemCatalog я позиционира).
 * БДС | Фонетична | ABC записва подредбата в личните настройки (оптимистично,
 * без localStorage) — при следващо влизане на всяко устройство е същата.
 */
export const SearchKeyboard = ({
  value,
  matchedPrefixLength = value.length,
  layout,
  onInput,
  onBackspace,
  onClear,
  onHide,
}: SearchKeyboardProps) => {
  const { t } = useTranslation();
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  const rows = LAYOUT_ROWS[layout];
  const LAYOUT_LABELS: { key: KeyboardLayout; label: string }[] = [
    { key: "bds", label: t("ordering.kbBds") },
    { key: "phonetic", label: t("ordering.kbPhonetic") },
    { key: "latin", label: t("ordering.kbLatin") },
  ];
  // Търсачката пише малки букви (както физическата клавиатура)
  const press = (key: string) => onInput(key.toLowerCase());
  // Клавишите не крадат фокуса (иначе клавиатурната машина на каталога спира)
  const noFocus = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className="rounded-[14px] border border-pos-line bg-pos-panel p-2.5 shadow-[0_-10px_30px_rgba(40,30,20,.14)] flex flex-col gap-2">
      {/* Мирор на търсачката: ТЪРСИ | стойност … подсказка + смяна на азбуката */}
      <div className="flex items-center gap-2">
        <div
          className={`flex-1 min-w-0 h-11 rounded-[10px] border bg-pos-card px-3 flex items-center gap-2.5 ${
            matchedPrefixLength < value.length
              ? "border-pos-danger-ink"
              : "border-pos-line"
          }`}
        >
          <span className="font-pos-mono text-[11px] font-semibold tracking-[.1em] uppercase text-pos-sec-head shrink-0">
            {t("ordering.searchKicker")}
          </span>
          <span aria-hidden className="w-px h-5 bg-pos-line shrink-0" />
          <span className="flex-1 min-w-0 truncate text-[15px] font-semibold text-pos-ink">
            {value.slice(0, matchedPrefixLength)}
            <span className="text-pos-danger-ink">
              {value.slice(matchedPrefixLength)}
            </span>
            <span className="inline-block w-px h-4 bg-pos-accent align-middle ml-px animate-pulse" />
          </span>
          {!value && (
            <span className="font-pos-mono text-[11px] text-pos-muted shrink-0">
              {t("ordering.searchHint")}
            </span>
          )}
        </div>
        {/* БДС | Фонетична | ABC — сегментиран, активната е маркирана */}
        <span className="inline-flex h-11 items-stretch rounded-[10px] border border-pos-ctl bg-pos-card overflow-hidden shrink-0">
          {LAYOUT_LABELS.map((option, index) => (
            <button
              key={option.key}
              type="button"
              onMouseDown={noFocus}
              onClick={() => {
                if (option.key !== layout) {
                  updateUserConfiguration({ keyboardLayout: option.key });
                }
              }}
              aria-pressed={layout === option.key}
              className={`px-3 text-[13px] font-semibold font-pos-mono cursor-pointer select-none transition-colors ${
                index > 0 ? "border-l border-pos-ctl" : ""
              } ${
                layout === option.key
                  ? "bg-pos-marked text-pos-accent-ink"
                  : "text-pos-ink2 hover:bg-pos-panel"
              }`}
            >
              {option.label}
            </button>
          ))}
        </span>
      </div>

      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="flex gap-1.5">
          {row.map((key) => (
            <button
              key={key}
              type="button"
              onMouseDown={noFocus}
              onClick={() => press(key)}
              className={`${KEY} flex-1`}
            >
              {key}
            </button>
          ))}
          {rowIndex === 0 && (
            <button
              type="button"
              onMouseDown={noFocus}
              onClick={onBackspace}
              aria-label={t("ordering.backspace")}
              className={`${ACTION} min-w-[92px] grid place-items-center text-[17px]`}
            >
              ⌫
            </button>
          )}
          {rowIndex === 1 && (
            <button
              type="button"
              onMouseDown={noFocus}
              onClick={() => onInput(" ")}
              className={`${ACTION} min-w-[92px]`}
            >
              {t("ordering.space")}
            </button>
          )}
          {rowIndex === 2 && (
            <button
              type="button"
              onMouseDown={noFocus}
              onClick={onClear}
              className={`${ACTION} min-w-[92px] text-pos-danger-ink inline-flex items-center justify-center gap-1.5`}
            >
              <DeleteOutlined className="text-[12px]" />
              {t("ordering.clearSearch")}
            </button>
          )}
        </div>
      ))}

      <div className="flex gap-1.5">
        {DIGITS.map((digit) => (
          <button
            key={digit}
            type="button"
            onMouseDown={noFocus}
            onClick={() => press(digit)}
            className={`${KEY} flex-1 font-pos-mono`}
          >
            {digit}
          </button>
        ))}
        <button
          type="button"
          onMouseDown={noFocus}
          onClick={onHide}
          className="h-11 min-w-[92px] rounded-[10px] border border-pos-accent bg-pos-accent px-3 text-[13px] font-bold text-white cursor-pointer select-none inline-flex items-center justify-center gap-1.5 hover:bg-pos-accent-ink transition-colors"
        >
          {t("ordering.hideKeyboard")}
          <DownOutlined className="text-[10px]" />
        </button>
      </div>
    </div>
  );
};
